import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";

export function Reveal({ children, delay = 0, className = "", y = 24 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -60px 0px" });
  const [fallback, setFallback] = useState(false);

  // Guarantee content becomes visible even if the IntersectionObserver
  // never fires (e.g. fast route changes / scroll restoration).
  useEffect(() => {
    const t = setTimeout(() => setFallback(true), 500);
    return () => clearTimeout(t);
  }, []);

  const visible = inView || fallback;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Eyebrow({ children, className = "" }) {
  return (
    <span
      className={`inline-block font-body text-xs uppercase tracking-[0.28em] text-brand-lavender-dark ${className}`}
    >
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, intro, center = false, className = "" }) {
  return (
    <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-3xl"} ${className}`}>
      {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
      <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-brand-ink leading-[1.1]">
        {title}
      </h2>
      {intro && (
        <p className="mt-6 font-body text-base md:text-lg text-brand-slate leading-relaxed">
          {intro}
        </p>
      )}
    </div>
  );
}

export function PrimaryButton({ as: As = "button", className = "", children, ...props }) {
  return (
    <As
      className={`inline-flex items-center justify-center gap-2 bg-brand-forest-fresh text-white rounded-full px-6 py-3 font-body text-sm tracking-wide shadow-sm hover:bg-brand-forest transition-colors duration-300 ${className}`}
      {...props}
    >
      {children}
    </As>
  );
}

export function SecondaryButton({ as: As = "button", className = "", children, ...props }) {
  return (
    <As
      className={`inline-flex items-center justify-center gap-2 bg-brand-lavender-light border border-brand-lavender-light text-brand-forest-dark rounded-full px-6 py-3 font-body text-sm tracking-wide hover:bg-brand-lavender-deep hover:text-white hover:border-brand-lavender-deep transition-colors duration-300 ${className}`}
      {...props}
    >
      {children}
    </As>
  );
}
